import styled from '@emotion/styled';
import { Checkbox, Field, Stack, Text, Textarea, TextInput, tokens } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import type { TFunction } from 'i18next';
import type { SurveyInvitationProps } from '../Invitation';

const Container = styled(Stack)`
  flex-basis: 40%;
  flex-direction: column;
  gap: ${tokens.spacer.x2};
  min-width: fit-content;
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${tokens.spacer.x3};
  gap: ${tokens.spacer.x5};
`;

type FieldProps = {
  name: string;
  value: string;
  maxLength: number;
  type?: 'input' | 'textarea';
  onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  t: TFunction<'feedback', 'wizard.section.invitation', 'feedback'>;
};

const InvitationField = ({
  t,
  name,
  type = 'input',
  value,
  onChange,
  maxLength,
}: FieldProps): JSX.Element => {
  const isInvalid = value.length === 0;
  return (
    <Field
      inputId={name}
      labelText={t(`fields.${name}`)}
      isInvalid={isInvalid}
      showInvalidIcon
      invalidText={t('fields.empty-field-error')}
      isRequired
    >
      {type === 'input' ? (
        <TextInput
          id={name}
          placeholder={t(`fields.${name}-placeholder`)}
          value={value}
          onChange={onChange}
          isRequired
          maxLength={maxLength}
        />
      ) : (
        <Textarea
          id={name}
          name={name}
          value={value}
          placeholder={t(`fields.${name}-placeholder`)}
          onChange={onChange}
          isRequired
          maxLength={maxLength}
          rows={4}
        />
      )}
    </Field>
  );
};

export const InvitationCustomization = ({
  invitation,
  isInvitationEnabled,
  setInviteEnabled,
  onInvitationChange,
}: Omit<SurveyInvitationProps, 'showBranding'>): JSX.Element => {
  const { t } = useTranslation('feedback', { keyPrefix: 'wizard.section.invitation' });
  const { title, description, button, close } = invitation;

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      target: { value, id },
    } = event;

    onInvitationChange({ ...invitation, [id]: value });
  };

  return (
    <Container>
      <Checkbox
        isChecked={isInvitationEnabled}
        labelContent={
          <Text as="span" textType="subtitle2">
            {t('toggle-title')}
          </Text>
        }
        helperText={t('toggle-description')}
        onChange={() => setInviteEnabled(!isInvitationEnabled)}
      />
      {isInvitationEnabled && (
        <FieldsContainer>
          <InvitationField
            name="title"
            value={title}
            onChange={handleValueChange}
            maxLength={100}
            t={t}
          />
          <InvitationField
            name="description"
            value={description}
            onChange={handleValueChange}
            maxLength={180}
            type="textarea"
            t={t}
          />
          <InvitationField
            name="button"
            value={button}
            onChange={handleValueChange}
            maxLength={60}
            t={t}
          />
          <InvitationField
            name="close"
            value={close}
            onChange={handleValueChange}
            maxLength={60}
            t={t}
          />
        </FieldsContainer>
      )}
    </Container>
  );
};
